"use client"

import * as React from "react"

import { SIDEBAR_BRAND_LOGO_COLLAPSED_SRC } from "@/constants/sidebarBrandLogo"
import { cn } from "@/lib/utils"

const DEFAULT_LENGTH = 6

/** Must cover stack → dissolve → logo settle + a few clean spin revolutions. */
const SUCCESS_ANIMATION_MS = 3400
const SUCCESS_ANIMATION_REDUCED_MS = 450

/** Intrinsic PNG is 1017² — pass full size so the browser never downsamples from a tiny bitmap. */
const SUCCESS_LOGO_INTRINSIC_PX = 1017
const SUCCESS_LOGO_SRC = SIDEBAR_BRAND_LOGO_COLLAPSED_SRC

export type MosaicOtpInputProps = {
  id?: string
  value: string
  onChange: (value: string) => void
  length?: number
  disabled?: boolean
  /** When true, tiles use error (PnL red) stroke. */
  invalid?: boolean
  /**
   * Verification accepted — tiles stack into one center diamond (appearance unchanged),
   * then dissolve while the logo fills that exact tile silhouette and spins.
   * `onSuccessAnimationComplete` fires when the sequence finishes.
   */
  success?: boolean
  onSuccessAnimationComplete?: () => void
  autoFocus?: boolean
  className?: string
  "aria-label"?: string
  "aria-describedby"?: string
}

function digitsOnly(raw: string): string {
  return raw.replace(/\D/g, "")
}

function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false
  }
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches
}

/**
 * Mosaic-tile OTP: each digit sits in a 45° rhombus (square rotated like workspace tiles).
 * Focused tile: dashed Tropical Surf border.
 * Success: tiles stack into one center diamond, which becomes the spinning brand mark.
 */
export function MosaicOtpInput({
  id = "mosaic-otp",
  value,
  onChange,
  length = DEFAULT_LENGTH,
  disabled = false,
  invalid = false,
  success = false,
  onSuccessAnimationComplete,
  autoFocus = false,
  className,
  "aria-label": ariaLabel = "Verification code",
  "aria-describedby": ariaDescribedBy,
}: MosaicOtpInputProps): React.ReactElement {
  const inputRefs = React.useRef<Array<HTMLInputElement | null>>([])
  const [focusIndex, setFocusIndex] = React.useState<number | null>(autoFocus ? 0 : null)
  const completeFiredRef = React.useRef(false)
  const onSuccessCompleteRef = React.useRef(onSuccessAnimationComplete)
  onSuccessCompleteRef.current = onSuccessAnimationComplete

  const digits = React.useMemo(() => {
    const cleaned = digitsOnly(value).slice(0, length)
    return Array.from({ length }, (_, i) => cleaned[i] ?? "")
  }, [value, length])

  const commitDigits = React.useCallback(
    (next: string[]) => {
      onChange(next.join("").slice(0, length))
    },
    [length, onChange]
  )

  const focusAt = React.useCallback((index: number) => {
    const clamped = Math.max(0, Math.min(length - 1, index))
    inputRefs.current[clamped]?.focus()
    setFocusIndex(clamped)
  }, [length])

  React.useEffect(() => {
    if (autoFocus && !disabled && !success) {
      focusAt(0)
    }
  }, [autoFocus, disabled, success, focusAt])

  // Decode brand mark early so the success morph never flashes a low-res paint.
  React.useEffect(() => {
    if (typeof window === "undefined") return
    const img = new window.Image()
    img.decoding = "async"
    img.src = SUCCESS_LOGO_SRC
  }, [])

  React.useEffect(() => {
    if (!success || completeFiredRef.current) return
    const delay = prefersReducedMotion() ? SUCCESS_ANIMATION_REDUCED_MS : SUCCESS_ANIMATION_MS
    const timer = window.setTimeout(() => {
      if (completeFiredRef.current) return
      completeFiredRef.current = true
      onSuccessCompleteRef.current?.()
    }, delay)
    return () => window.clearTimeout(timer)
    // Only re-run when `success` turns on — parent callback identity must not reset the timer.
  }, [success])

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault()
    if (disabled || success) return
    const pasted = digitsOnly(event.clipboardData.getData("text")).slice(0, length)
    if (!pasted) return
    const next = Array.from({ length }, (_, i) => pasted[i] ?? "")
    commitDigits(next)
    focusAt(Math.min(pasted.length, length - 1))
  }

  const handleChange = (index: number, raw: string) => {
    if (disabled || success) return
    const cleaned = digitsOnly(raw)
    if (cleaned.length > 1) {
      // Mobile autofill / multi-char paste into one box
      const next = Array.from({ length }, (_, i) => cleaned[i] ?? digits[i] ?? "")
      // Prefer filling from this index when paste lands mid-row
      if (cleaned.length <= length - index) {
        for (let i = 0; i < cleaned.length; i += 1) {
          next[index + i] = cleaned[i]
        }
      }
      commitDigits(next)
      focusAt(Math.min(index + cleaned.length, length - 1))
      return
    }

    const next = [...digits]
    next[index] = cleaned.slice(-1)
    commitDigits(next)
    if (cleaned) focusAt(index + 1)
  }

  const handleKeyDown = (index: number, event: React.KeyboardEvent<HTMLInputElement>) => {
    if (disabled || success) return
    if (event.key === "Backspace") {
      if (digits[index]) {
        const next = [...digits]
        next[index] = ""
        commitDigits(next)
        return
      }
      if (index > 0) {
        event.preventDefault()
        const next = [...digits]
        next[index - 1] = ""
        commitDigits(next)
        focusAt(index - 1)
      }
      return
    }
    if (event.key === "ArrowLeft") {
      event.preventDefault()
      focusAt(index - 1)
      return
    }
    if (event.key === "ArrowRight") {
      event.preventDefault()
      focusAt(index + 1)
    }
  }

  const centerIndex = (length - 1) / 2

  return (
    <div
      data-mosaic-otp=""
      data-state={success ? "success" : undefined}
      role="group"
      aria-label={success ? "Verification successful" : ariaLabel}
      aria-describedby={ariaDescribedBy}
      aria-busy={success || undefined}
      className={cn(
        // One unbroken row of 6 tiles — do not wrap (max-w-sm cards are tight at sm:size-14).
        "relative inline-flex w-max max-w-full flex-nowrap items-center justify-center gap-x-2 sm:gap-x-2.5",
        success && "pointer-events-none",
        className
      )}
    >
      {digits.map((digit, index) => {
        const isFocused = !success && focusIndex === index
        const isFilled = digit.length > 0
        return (
          <div
            key={`${id}-slot-${index}`}
            data-mosaic-otp-slot=""
            data-state={
              success
                ? "success"
                : invalid
                  ? "invalid"
                  : isFocused
                    ? "focused"
                    : isFilled
                      ? "filled"
                      : "empty"
            }
            className="relative flex size-11 shrink-0 items-center justify-center sm:size-12"
            style={
              {
                "--mosaic-otp-slot-i": index,
                "--mosaic-otp-center-i": centerIndex,
              } as React.CSSProperties
            }
          >
            {/*
              Outer host keeps layout axis-aligned; the tile is a square rotated 45°
              (rhombus / mosaic diamond). Digit input is counter-rotated so type stays upright.
            */}
            <div
              data-mosaic-otp-tile=""
              className={cn(
                "flex size-[1.85rem] rotate-45 items-center justify-center rounded-[6px] sm:size-[2.05rem]",
                "bg-content-canvas dark:bg-sidebar",
                "transition-[border-color,border-style] duration-150"
              )}
            >
              <input
                ref={(el) => {
                  inputRefs.current[index] = el
                }}
                id={index === 0 ? id : undefined}
                type="text"
                inputMode="numeric"
                autoComplete={index === 0 ? "one-time-code" : "off"}
                name={index === 0 ? "otp" : undefined}
                maxLength={length}
                disabled={disabled || success}
                value={digit}
                tabIndex={success ? -1 : undefined}
                aria-label={`Digit ${index + 1} of ${length}`}
                className={cn(
                  "size-full -rotate-45 bg-transparent text-center",
                  "font-[family-name:var(--font-primary)] text-base font-medium sm:text-lg",
                  "text-gray-500 outline-none dark:!text-gray-500",
                  "caret-[color:var(--brand-tropical-surf)]",
                  "disabled:cursor-not-allowed disabled:opacity-50"
                )}
                onFocus={() => setFocusIndex(index)}
                onBlur={() =>
                  setFocusIndex((prev) => (prev === index ? null : prev))
                }
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={handlePaste}
              />
            </div>
          </div>
        )
      })}

      {success ? (
        <div
          data-mosaic-otp-logo=""
          className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center"
          aria-hidden
        >
          {/*
            Same mosaic diamond as a tile. Reveal + spin are on separate nodes so
            transforms don't clobber each other. Logo fills the vessel edge-to-edge.
          */}
          <div data-mosaic-otp-logo-reveal="">
            <div data-mosaic-otp-logo-spin="">
              <div data-mosaic-otp-logo-vessel="">
                <img
                  src={SUCCESS_LOGO_SRC}
                  alt=""
                  width={SUCCESS_LOGO_INTRINSIC_PX}
                  height={SUCCESS_LOGO_INTRINSIC_PX}
                  decoding="async"
                  fetchPriority="high"
                  draggable={false}
                />
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}

export default MosaicOtpInput
